/**
 * /dashboard/calendar — personal calendar view (Time & Leave module
 * floor surface, SOP §1.2; 2026-06-01 pivot).
 *
 * Every employee has a personal monthly calendar, regardless of the
 * tenant's `time_tracking` feature. The sidebar's Time & Leave entry
 * lands here by default.
 *
 * v1 scope (no new backend endpoints):
 *  - Month grid (Mon-Sun, 6 rows). Prev/Today/Next month navigation.
 *  - Public holidays from GET /api/v1/holidays?year=YYYY (Batch G2).
 *  - Approved leaves on each day from GET /api/v1/leave/requests
 *    (server already role-filters; `scope=self` narrows to the
 *    caller's own rows regardless of role).
 *  - Worked-day dot from GET /api/v1/timesheet/summary (gated on the
 *    tenant having `time_tracking`).
 *  - Today highlighted with an indigo ring.
 *  - Click a day → Dialog with the date, any holiday + any leave on
 *    that day, plus quick-action buttons (Request leave / Log hours).
 *
 * Pure-JS date math (no date-fns dep added on the page itself). All
 * dates are computed in the caller's local tz; the holiday API and
 * leave API both return YYYY-MM-DD strings (tz-stripped), so the only
 * conversion we do is string ↔ {year, month, day} — never
 * `new Date("YYYY-MM-DD")` which would interpret as UTC and could
 * drift a day.
 *
 * Migration note (2026-06-01, Next.js → Vite): port of
 * frontend/src/app/(dashboard)/dashboard/calendar/page.tsx. Swaps:
 *   - "use client" dropped
 *   - next/link → @tanstack/react-router Link
 *   - the per-module layout.tsx replaced by the shared
 *     TimeAndLeaveShell wrapper (DRY across calendar/leave/timesheet/
 *     approvals).
 */

import { useMemo, useState } from "react";
import { createFileRoute, Link , useParams} from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { CalendarClock, ChevronLeft, ChevronRight } from "lucide-react";
import { listHolidays } from "@/lib/api/holidays";
import { listLeaveRequests } from "@/lib/api/leave";
import { getTimesheetSummary } from "@/lib/api/timesheet";
import { useFeatures } from "@/lib/auth";
import { TimeAndLeaveShell } from "@/components/shared/time-and-leave-shell";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/calendar")({
  component: CalendarRoute,
});

// Phase 3b: Leave + Timesheet hubs are now registered, so the dialog
// footer's "Request leave" / "Log hours" CTAs can use the strict typed
// `<Link to=…>` form (no more runtime-string indirection). See the
// footer below — both `to=` are literal route paths now.

function CalendarRoute() {
  return (
    <TimeAndLeaveShell
      title="Time & Leave"
      description="Your month at a glance — holidays and approved leave."
    >
      <CalendarPage />
    </TimeAndLeaveShell>
  );
}

// ---------------------------------------------------------------------------
// Pure-JS date helpers — local tz, YYYY-MM-DD strings as the wire format.
// Avoid `new Date("YYYY-MM-DD")` which spec-interprets as UTC.
// ---------------------------------------------------------------------------

interface YMD {
  y: number;
  /** 1-12 (NOT 0-11, to match the wire format). */
  m: number;
  /** 1-31. */
  d: number;
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

function ymdToISO({ y, m, d }: YMD): string {
  return `${y}-${pad2(m)}-${pad2(d)}`;
}

function isoToYMD(iso: string): YMD | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!match) return null;
  return {
    y: Number(match[1]),
    m: Number(match[2]),
    d: Number(match[3]),
  };
}

function todayYMD(): YMD {
  const now = new Date();
  return { y: now.getFullYear(), m: now.getMonth() + 1, d: now.getDate() };
}

function daysInMonth(y: number, m: number): number {
  return new Date(y, m, 0).getDate();
}

function mondayIndex(y: number, m: number, d: number): number {
  const jsDay = new Date(y, m - 1, d).getDay();
  return (jsDay + 6) % 7;
}

function ymdEqual(a: YMD, b: YMD): boolean {
  return a.y === b.y && a.m === b.m && a.d === b.d;
}

function addMonths(
  { y, m }: { y: number; m: number },
  delta: number,
): { y: number; m: number } {
  const next = new Date(y, m - 1 + delta, 1);
  return { y: next.getFullYear(), m: next.getMonth() + 1 };
}

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

const WEEKDAY_LABELS = [
  "Mon",
  "Tue",
  "Wed",
  "Thu",
  "Fri",
  "Sat",
  "Sun",
] as const;

function formatLongDate({ y, m, d }: YMD): string {
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString(undefined, {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

// ---------------------------------------------------------------------------
// Data hooks
// ---------------------------------------------------------------------------

function useHolidaysForYear(year: number) {
  return useQuery({
    queryKey: ["holidays", "year", year],
    queryFn: ({ signal }) => listHolidays({ year }, signal),
    staleTime: 60 * 60 * 1000, // Holidays barely move; 1h cache is generous.
  });
}

function useApprovedLeaves() {
  return useQuery({
    queryKey: ["leave", "requests", "approved", "self"],
    queryFn: ({ signal }) =>
      listLeaveRequests({ status: "approved", scope: "self" }, signal),
    staleTime: 5 * 60 * 1000,
  });
}

function useTimesheetSummary(
  focus: { y: number; m: number },
  hasTimeTracking: boolean,
) {
  const prev = addMonths(focus, -1);
  const next = addMonths(focus, +1);
  const from = ymdToISO({
    y: prev.y,
    m: prev.m,
    d: Math.max(1, daysInMonth(prev.y, prev.m) - 6),
  });
  const to = ymdToISO({ y: next.y, m: next.m, d: 7 });
  return useQuery({
    queryKey: ["timesheet", "summary", from, to],
    queryFn: ({ signal }) => getTimesheetSummary(from, to, signal),
    enabled: hasTimeTracking,
    staleTime: 5 * 60 * 1000,
  });
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

interface DayCellData {
  ymd: YMD;
  outside: boolean;
  holidayName: string | null;
  leaveCode: string | null;
  leaveTypeId: number | null;
  workedMinutes: number;
}

const SKELETON_CELL_KEYS = Array.from({ length: 42 }, (_, i) => `sk-${i}`);

function CalendarPage() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const { has } = useFeatures();
  const hasTimeTracking = has("time_tracking");

  const [focus, setFocus] = useState<{ y: number; m: number }>(() => {
    const t = todayYMD();
    return { y: t.y, m: t.m };
  });
  const [selected, setSelected] = useState<YMD | null>(null);

  const today = todayYMD();
  const holidaysQ = useHolidaysForYear(focus.y);
  const leavesQ = useApprovedLeaves();
  const summaryQ = useTimesheetSummary(focus, hasTimeTracking);

  const cells = useMemo<DayCellData[]>(
    () =>
      buildCells({
        focus,
        holidays: holidaysQ.data?.items ?? [],
        leaves: leavesQ.data?.leave_requests ?? [],
        summary: summaryQ.data?.items ?? [],
      }),
    [focus, holidaysQ.data, leavesQ.data, summaryQ.data],
  );

  const isLoading = holidaysQ.isLoading || leavesQ.isLoading;
  const monthLabel = `${MONTH_NAMES[focus.m - 1]} ${focus.y}`;

  const goPrev = () => setFocus((f) => addMonths(f, -1));
  const goNext = () => setFocus((f) => addMonths(f, +1));
  const goToday = () => {
    const t = todayYMD();
    setFocus({ y: t.y, m: t.m });
  };

  const selectedDetail = useMemo(() => {
    if (!selected) return null;
    const iso = ymdToISO(selected);
    const holiday = (holidaysQ.data?.items ?? []).find(
      (h) => h.is_active && h.holiday_date.slice(0, 10) === iso,
    );
    const leave = (leavesQ.data?.leave_requests ?? []).find((r) => {
      const s = isoToYMD(r.start_date);
      const e = isoToYMD(r.end_date);
      if (!s || !e) return false;
      const sJS = new Date(s.y, s.m - 1, s.d).getTime();
      const eJS = new Date(e.y, e.m - 1, e.d).getTime();
      const curJS = new Date(
        selected.y,
        selected.m - 1,
        selected.d,
      ).getTime();
      return curJS >= sJS && curJS <= eJS;
    });
    const workedMinutes =
      (summaryQ.data?.items ?? []).find(
        (e) => e.date.slice(0, 10) === iso,
      )?.minutes ?? 0;
    return { holiday, leave, workedMinutes, iso };
  }, [selected, holidaysQ.data, leavesQ.data, summaryQ.data]);

  return (
    <Card>
      <CardContent className="space-y-3 pt-4">
        {/* Month controls — compact row */}
        <div className="flex items-center justify-between gap-3">
          <h2
            className="text-base font-semibold tracking-tight text-gray-900"
            data-testid="calendar-current-month-label"
          >
            {monthLabel}
          </h2>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              className="h-7 w-7"
              onClick={goPrev}
              aria-label="Previous month"
              data-testid="calendar-prev"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={goToday}
              data-testid="calendar-today"
            >
              Today
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-7 w-7"
              onClick={goNext}
              aria-label="Next month"
              data-testid="calendar-next"
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>

        {/* Weekday header + day cells */}
        <div className="grid grid-cols-7 gap-px overflow-hidden rounded-md border bg-border">
          {WEEKDAY_LABELS.map((label) => (
            <div
              key={label}
              className="bg-muted/40 px-1.5 py-1 text-center text-[10px] font-medium uppercase tracking-wide text-muted-foreground"
            >
              {label}
            </div>
          ))}

          {isLoading
            ? SKELETON_CELL_KEYS.map((k) => (
                <div key={k} className="h-14 bg-white p-1 sm:h-16">
                  <Skeleton className="h-full w-full" />
                </div>
              ))
            : cells.map((cell) => {
                const iso = ymdToISO(cell.ymd);
                const isToday = ymdEqual(cell.ymd, today);
                const chipLabel = cell.holidayName ?? cell.leaveCode;
                const chipClass = cell.holidayName
                  ? "bg-blue-100 text-blue-800"
                  : "bg-orange-100 text-orange-800";
                return (
                  <button
                    key={iso}
                    type="button"
                    onClick={() => setSelected(cell.ymd)}
                    data-testid={`calendar-day-${iso}`}
                    aria-label={`${formatLongDate(cell.ymd)}${
                      cell.holidayName
                        ? `, holiday: ${cell.holidayName}`
                        : ""
                    }${cell.leaveCode ? ", approved leave" : ""}`}
                    className={cn(
                      "group relative h-14 overflow-hidden bg-white p-1 text-left text-xs transition-colors hover:bg-accent/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 sm:h-16",
                      cell.outside && "bg-muted/30 text-muted-foreground",
                      isToday && "ring-2 ring-primary ring-inset",
                    )}
                  >
                    <span
                      className={cn(
                        "block text-xs font-medium tabular-nums leading-tight",
                        cell.outside
                          ? "text-muted-foreground"
                          : "text-gray-900",
                      )}
                    >
                      {cell.ymd.d}
                    </span>
                    {chipLabel && (
                      <span
                        className={cn(
                          "mt-1 line-clamp-1 inline-block rounded-sm px-1 py-0.5 text-[9px] font-medium leading-none",
                          chipClass,
                        )}
                      >
                        {chipLabel}
                      </span>
                    )}
                    {cell.holidayName && cell.leaveCode && (
                      <span className="absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-orange-500" />
                    )}
                    {cell.workedMinutes > 0 && (
                      <span
                        className="absolute bottom-1 right-1 h-1.5 w-1.5 rounded-full bg-emerald-500"
                        aria-label={`Logged ${cell.workedMinutes} minutes`}
                      />
                    )}
                  </button>
                );
              })}
        </div>

        {/* Day-detail dialog */}
        <Dialog
          open={selected !== null}
          onOpenChange={(open) => {
            if (!open) setSelected(null);
          }}
        >
          <DialogContent className="sm:max-w-md">
            {selected && selectedDetail && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <CalendarClock className="h-4 w-4 text-muted-foreground" />
                    {formatLongDate(selected)}
                  </DialogTitle>
                  <DialogDescription>
                    {selectedDetail.holiday ||
                    selectedDetail.leave ||
                    selectedDetail.workedMinutes > 0
                      ? "Details for this day:"
                      : "Nothing scheduled on this day."}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-3 text-sm">
                  {selectedDetail.holiday && (
                    <div className="rounded-md border border-blue-200 bg-blue-50 p-3">
                      <p className="font-medium text-blue-900">
                        {selectedDetail.holiday.name}
                      </p>
                      {selectedDetail.holiday.description && (
                        <p className="mt-1 text-blue-800/80">
                          {selectedDetail.holiday.description}
                        </p>
                      )}
                      <p className="mt-1 text-xs uppercase tracking-wide text-blue-700/70">
                        Public holiday
                      </p>
                    </div>
                  )}
                  {selectedDetail.leave && (
                    <div className="rounded-md border border-orange-200 bg-orange-50 p-3">
                      <p className="font-medium text-orange-900">
                        Approved leave
                      </p>
                      <p className="mt-1 text-xs text-orange-800/80">
                        {selectedDetail.leave.start_date} →{" "}
                        {selectedDetail.leave.end_date} ·{" "}
                        {selectedDetail.leave.total_days} day(s)
                      </p>
                      {selectedDetail.leave.reason && (
                        <p className="mt-1 text-orange-800/80">
                          {selectedDetail.leave.reason}
                        </p>
                      )}
                    </div>
                  )}
                  {selectedDetail.workedMinutes > 0 && (
                    <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3">
                      <p className="font-medium text-emerald-900">
                        {(selectedDetail.workedMinutes / 60).toFixed(1)}h
                        logged
                      </p>
                      <p className="mt-1 text-xs text-emerald-800/80">
                        From your timesheet entries.
                      </p>
                    </div>
                  )}
                </div>
                <DialogFooter className="flex-col gap-2 sm:flex-row">
                  {/* Both routes are now registered (Phase 3b);
                      strict typed `<Link to=…>` — TanStack Router
                      type-checks the destination paths. */}
                  <Button
                    asChild
                    variant="outline"
                    className="w-full sm:w-auto"
                  >
                    <Link to="/app/$tenantSlug/leave/new" params={{ tenantSlug }}>Request leave</Link>
                  </Button>
                  {hasTimeTracking && (
                    <Button asChild className="w-full sm:w-auto">
                      <Link to="/app/$tenantSlug/timesheet" params={{ tenantSlug }}>Log hours</Link>
                    </Button>
                  )}
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Cell builder — extracted from the component body so the JSX stays
// readable and the pure data step can be unit-tested in isolation.
// ---------------------------------------------------------------------------

interface BuildCellsInput {
  focus: { y: number; m: number };
  holidays: ReadonlyArray<{
    holiday_date: string;
    name: string;
    is_active: boolean;
  }>;
  leaves: ReadonlyArray<{
    start_date: string;
    end_date: string;
    leave_type_id: number;
  }>;
  summary: ReadonlyArray<{ date: string; minutes: number }>;
}

function buildCells({
  focus,
  holidays,
  leaves,
  summary,
}: BuildCellsInput): DayCellData[] {
  const firstWeekday = mondayIndex(focus.y, focus.m, 1);
  const totalDays = daysInMonth(focus.y, focus.m);
  const prevMonth = addMonths(focus, -1);
  const nextMonth = addMonths(focus, +1);
  const prevDays = daysInMonth(prevMonth.y, prevMonth.m);

  const holidayMap = new Map<string, string>();
  for (const h of holidays) {
    if (!h.is_active) continue;
    holidayMap.set(h.holiday_date.slice(0, 10), h.name);
  }

  const workedMap = new Map<string, number>();
  for (const e of summary) {
    workedMap.set(e.date.slice(0, 10), e.minutes);
  }

  const leaveMap = new Map<string, { code: string; typeId: number }>();
  for (const r of leaves) {
    const start = isoToYMD(r.start_date);
    const end = isoToYMD(r.end_date);
    if (!start || !end) continue;
    const startJS = new Date(start.y, start.m - 1, start.d);
    const endJS = new Date(end.y, end.m - 1, end.d);
    for (
      let cur = new Date(startJS);
      cur.getTime() <= endJS.getTime();
      cur.setDate(cur.getDate() + 1)
    ) {
      const iso = ymdToISO({
        y: cur.getFullYear(),
        m: cur.getMonth() + 1,
        d: cur.getDate(),
      });
      if (!leaveMap.has(iso)) {
        leaveMap.set(iso, { code: "LV", typeId: r.leave_type_id });
      }
    }
  }

  const out: DayCellData[] = [];

  // Leading padding from prev month.
  for (let i = 0; i < firstWeekday; i++) {
    const d = prevDays - firstWeekday + 1 + i;
    const ymd = { y: prevMonth.y, m: prevMonth.m, d };
    const iso = ymdToISO(ymd);
    out.push({
      ymd,
      outside: true,
      holidayName: holidayMap.get(iso) ?? null,
      leaveCode: leaveMap.get(iso)?.code ?? null,
      leaveTypeId: leaveMap.get(iso)?.typeId ?? null,
      workedMinutes: workedMap.get(iso) ?? 0,
    });
  }

  // Focused month.
  for (let d = 1; d <= totalDays; d++) {
    const ymd = { y: focus.y, m: focus.m, d };
    const iso = ymdToISO(ymd);
    out.push({
      ymd,
      outside: false,
      holidayName: holidayMap.get(iso) ?? null,
      leaveCode: leaveMap.get(iso)?.code ?? null,
      leaveTypeId: leaveMap.get(iso)?.typeId ?? null,
      workedMinutes: workedMap.get(iso) ?? 0,
    });
  }

  // Trailing padding to fill 6 rows (42 cells).
  while (out.length < 42) {
    const idx = out.length - firstWeekday - totalDays + 1;
    const ymd = { y: nextMonth.y, m: nextMonth.m, d: idx };
    const iso = ymdToISO(ymd);
    out.push({
      ymd,
      outside: true,
      holidayName: holidayMap.get(iso) ?? null,
      leaveCode: leaveMap.get(iso)?.code ?? null,
      leaveTypeId: leaveMap.get(iso)?.typeId ?? null,
      workedMinutes: workedMap.get(iso) ?? 0,
    });
  }
  return out;
}
